import axios from 'axios';
import cuid from 'cuid';
import PropTypes from 'prop-types';
import React, { useContext, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';

import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Col,
  DataGrid,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  Paper,
  Row,
  Typography,
} from 'components/AORedesign';
import { currencyStringFormatter } from 'helpers';
import { GET_BILLING_EXACT_SEARCH, GET_CLAIMS_DETAILS, GET_CLAIMS_DETAILS_GEN_REPORT_URL, POLICY_NUMBER_URL } from '../../constants/api';
import { ROUTE_BILLING, ROUTE_BILLING_DETAILS } from '../../constants/routes';
import AppDataContext from '../../contexts/appData.context';
import { dataGridCurrencyFormatter } from '../../helpers';
import { handlePdfDownload } from '../CommonUtilityServices/DownloadPdfService';
import DetailRow from './DetailRow';
import DetailRowMulti from './DetailRowMulti';

const ClaimsInquiryDetails = ({ selectedClaim, handleCloseClaimsInquiryDetails }) => {
  const [loading, setLoading] = useState(true);
  const [claimsDetails, setClaimsDetails] = useState();
  const [downloadingPdfError, setDownloadingPdfError] = useState(false);
  const [viewPaymentDetails, setViewPaymentDetails] = useState();

  const { setPreviousPageName } = useContext(AppDataContext);
  const history = useHistory();

  const SHOW_PAYMENT_DETAILS = typeof viewPaymentDetails === 'object';

  useEffect(() => {
    (async () => {
      if (selectedClaim) {
        try {
          const { policyNumber, occurrenceNumber } = selectedClaim;
          const getClaimsDetailUrl = `${GET_CLAIMS_DETAILS}/${policyNumber}/${occurrenceNumber}`;
          const details = await axios.get(getClaimsDetailUrl);
          setClaimsDetails(details.data);
        } catch (error) {
          console.error(error);
        } finally {
          setLoading(false);
        }
      }
    })();
  }, [selectedClaim]);

  const {
    name1,
    policyEffectiveDate,
    totalAmountPaid,
    dateOfLoss,
    lossDescription,
    vehicleYear,
    vehicleMake,
    vehicleModel,
    driverName,
    lossLoc_Addr1,
    lossLoc_Addr2,
    lossLoc_City,
    lossLoc_County,
    lossLoc_State,
    lossLoc_Zipcode,
    totalOutstReserve,
    totalExpensesPaid,
    subrogation,
    adjusterFullname,
    adjusterMissingMessage,
    accountagent,
    policyNumber,
    occurrenceNumber,
    dateReported,
    reportedBy,
    howReported,
    dateClosed,
  } = claimsDetails?.claimSummary || {};

  const adjusterNotAvailable = !claimsDetails?.claimSummary?.adjusterFullname;
  const adjusterPhoneNumber = claimsDetails?.claimSummary?.adjusterFullname && claimsDetails?.claimSummary?.adjusterPhoneNumber;
  const adjusterEmail = claimsDetails?.claimSummary?.adjusterFullname && claimsDetails?.claimSummary?.adjusterEmail;
  const showClaimVehicleDetails = claimsDetails?.claimSummary?.lobDesc === 'Auto' && claimsDetails?.claimSummary?.vehicleYear != '-2';
  const showAdjusterMissingMessage = !claimsDetails?.claimSummary?.adjusterFullname || !claimsDetails?.claimSummary?.adjusterPhoneNumber;

  const transactionHistoryColumns = [
    {
      field: 'claimantName1',
      headerName: 'Claimant',
      width: 200,
    },
    {
      field: 'lossTypeDescr',
      headerName: 'Loss Type',
      width: 200,
    },
    {
      field: 'transactionDescr',
      headerName: 'Transaction',
      width: 200,
    },
    {
      field: 'issueDate',
      headerName: 'Issue Date',
      width: 200,
      filterable: false,
      type: 'date',
    },
    {
      field: 'payeeName',
      headerName: 'Payee Name',
      width: 200,
    },
    {
      field: 'checkNumber',
      headerName: 'Check #',
      width: 200,
    },
    {
      field: 'amount',
      headerName: 'Amount',
      width: 200,
      valueFormatter: dataGridCurrencyFormatter,
      renderCell: (params) => {
        const renderValue = params?.formattedValue;
        if (params.row.amount && params.row.transactionCode === 'LossPayment') {
          return <Link onClick={() => paymentDetailsOnClick(params.row)}>{renderValue}</Link>;
        } else {
          return renderValue;
        }
      },
    },
  ];

  const handleExportOnClick = () => {
    const request_body = {
      Area: 'claims-details',
      DetailedContext: [
        {
          name: 'policyNumber',
          value: policyNumber,
        },
        {
          name: 'occurrence',
          value: occurrenceNumber,
        },
      ],
      TypeOfRedirect: 'Generate-PDF',
    };
    const reportsGenerateUrl = GET_CLAIMS_DETAILS_GEN_REPORT_URL;
    handlePdfDownload(
      'POST',
      request_body,
      reportsGenerateUrl,
      () => {},
      setDownloadingPdfError,
      () => {},
      true,
    );
  };

  const paymentDetailsOnClick = (row) => {
    setViewPaymentDetails({
      'Payee Name1': row.payeeName,
      'Payee Name2': row.payeeName2,
      'Mailing Address': `${row.payeeAddress1} ${row.payeeAddress2} ${row.payeeCity} ${row.payeeState} ${row.payeeZipcode}`,
      'In Payment Of': row.inPaymentOf,
      'Check Amount': row.amount,
      'Check Number': row.checkNumber,
      'Check Status': row.checkStatus,
      'Issue Date': row.issueDate,
      'Date Check Cleared Bank': row.checkClearedDate,
    });
  };

  const handleBackToSummary = () => {
    setViewPaymentDetails();
  };

  const handleOnClickToBilling = async () => {
    setPreviousPageName('Claims Home');
    const policyNumber = selectedClaim.policyNumber;

    const results = await axios.get(`${GET_BILLING_EXACT_SEARCH}${POLICY_NUMBER_URL}/${policyNumber}/1`);
    if (results.length === 1) {
      history.push(`${ROUTE_BILLING_DETAILS}/${results.data[0].accountNumber}`);
    } else {
      history.push(ROUTE_BILLING);
    }
  };

  return (
    <Dialog
      disableEnforceFocus
      open={typeof selectedClaim === 'object'}
      onClose={handleCloseClaimsInquiryDetails}
      aria-labelledby="claims-inquiry-details-title"
      aria-describedby="claims-inquiry-details-description"
      maxWidth="lg"
      fullWidth>
      <DialogTitle>{SHOW_PAYMENT_DETAILS ? 'Payment Details' : 'Claims Inquiry Service'}</DialogTitle>

      <DialogContent>
        {SHOW_PAYMENT_DETAILS ? (
          Object.entries(viewPaymentDetails).map(([key, value]) => <DetailRow key={key} label={key} value={value} />)
        ) : (
          <>
            {loading ? (
              <Box display="flex" justifyContent="center" height="60px">
                <CircularProgress />
              </Box>
            ) : (
              <>
                <Paper>
                  <Typography variant="h6">Claims Summary</Typography>

                  {downloadingPdfError && <Alert severity="error">{downloadingPdfError}</Alert>}

                  <Row justifyContent="flex-end">
                    <Col xs={6}>
                      <Box display="flex" justifyContent="center" mb={2}>
                        <Link onClick={handleExportOnClick} endIcon="pdf">
                          Export to PDF
                        </Link>
                      </Box>
                    </Col>
                  </Row>

                  <Row spacing={2}>
                    <Col xs={6}>
                      <DetailRow label="Insured" value={name1} />
                      <DetailRow label="Effective Date" value={policyEffectiveDate} />
                      <DetailRow label="Date of Loss" value={dateOfLoss} />
                      <DetailRow label="Description of Loss" value={lossDescription} />

                      {showClaimVehicleDetails && (
                        <DetailRowMulti
                          label="Insured Vehicle"
                          values={[
                            `Year: ${vehicleYear}`,
                            `Make: ${vehicleMake}`,
                            `Model: ${vehicleModel}`,
                            `Insured Driver: ${driverName}`,
                            `State: ${lossLoc_State}`,
                            `Zip Code: ${lossLoc_Zipcode}`,
                          ]}
                        />
                      )}

                      <DetailRowMulti
                        label="Loss Location"
                        values={[
                          `Address 1: ${lossLoc_Addr1}`,
                          `Address 2: ${lossLoc_Addr2}`,
                          `City: ${lossLoc_City}`,
                          `County: ${lossLoc_County}`,
                          `State: ${lossLoc_State}`,
                          `Zip Code: ${lossLoc_Zipcode}`,
                        ]}
                      />

                      <DetailRow label="Total Outstanding Loss Reserve" value={currencyStringFormatter(totalOutstReserve)} />

                      <DetailRow label="Total Expenses Paid (Includes ALAE and ULAE)" value={currencyStringFormatter(totalExpensesPaid)} />

                      <DetailRow label="Subrogation" value={subrogation} />

                      <DetailRowMulti
                        label="Adjuster"
                        values={[
                          <Link key="claim-adjuster-name" href={`mailto:${adjusterFullname}`}>
                            {adjusterFullname}
                          </Link>,
                          ...(adjusterNotAvailable ? ['Not Available'] : []),
                          ...(adjusterPhoneNumber ? [adjusterPhoneNumber] : []),
                          ...(adjusterEmail ? [adjusterEmail] : []),
                          ...(showAdjusterMissingMessage ? [adjusterMissingMessage] : []),
                        ].filter(Boolean)}></DetailRowMulti>
                    </Col>

                    <Col xs={6}>
                      <DetailRow label="Agent" value={accountagent} />

                      <DetailRow label="Policy Number" value={<Link onClick={handleOnClickToBilling}>{policyNumber}</Link>} />

                      <DetailRow label="Occurrence Number" value={occurrenceNumber} />
                      <DetailRow label="Date Reported" value={dateReported} />
                      <DetailRow label="Reported By" value={reportedBy} />
                      <DetailRow label="How Reported" value={howReported} />
                      <DetailRow label="Date Closed" value={dateClosed} />
                      <DetailRow label="Total Loss Amount Paid" value={currencyStringFormatter(totalAmountPaid)} />
                    </Col>
                  </Row>
                </Paper>

                <Box mb={2} />

                <Paper>
                  <Typography variant="h6" style={{ marginBottom: 16 }}>
                    Transaction History
                  </Typography>

                  <Box>
                    <DataGrid
                      rows={
                        claimsDetails?.claimsDetailsTransactionHistory?.map((el) => ({
                          id: cuid(),
                          ...el,
                        })) ?? []
                      }
                      columns={transactionHistoryColumns}
                    />
                  </Box>
                </Paper>
              </>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions>
        {SHOW_PAYMENT_DETAILS ? (
          <Button onClick={handleBackToSummary}>Back to Claims Summary</Button>
        ) : (
          <Button onClick={handleCloseClaimsInquiryDetails}>Close</Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

ClaimsInquiryDetails.propTypes = {
  selectedClaim: PropTypes.object,
  handleCloseClaimsInquiryDetails: PropTypes.func,
};

export default ClaimsInquiryDetails;
